import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, shareReplay, tap, throwError } from 'rxjs';
import type { UserGroup } from '../authorization/models';
import type { AnswerTemplate } from '../../shared/models/question';

/**
 * Cache entry with TTL support.
 */
interface CacheEntry<T> {
  readonly data: T;
  readonly timestamp: number;
  readonly expiresAt: number;
}

/**
 * Cache state for a specific data type.
 */
interface CacheState<T> {
  readonly loading: boolean;
  readonly data: T | null;
  readonly error: string | null;
}

/** Default cache TTL: 24 hours in milliseconds */
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Signal-based API caching service.
 * Provides reactive caching for user groups and answer templates.
 *
 * NOTE: Lookup data caching is handled by LookupApiService using httpResource.
 * Components should use LookupDataService for all lookup data access.
 *
 * Features:
 * - Signal-based reactive cache state
 * - Automatic TTL-based cache expiration
 * - Type-safe caching for each data type
 * - Cache invalidation on mutations
 * - Request deduplication via shareReplay
 */
@Injectable({ providedIn: 'root' })
export class ApiCacheService {
  private readonly http = inject(HttpClient);

  // ============================================
  // User Groups Cache
  // ============================================

  private userGroupsCache: CacheEntry<readonly UserGroup[]> | null = null;
  private userGroupsRequest$: Observable<readonly UserGroup[]> | null = null;
  private readonly userGroupsState = signal<CacheState<readonly UserGroup[]>>({
    loading: false,
    data: null,
    error: null,
  });

  /** Read-only signal for user groups cache state */
  readonly userGroups = this.userGroupsState.asReadonly();

  /** Computed signal for just the user groups data */
  readonly userGroupsData = computed(() => this.userGroupsState().data ?? []);

  /** Computed signal for user groups loading state */
  readonly userGroupsLoading = computed(() => this.userGroupsState().loading);

  /**
   * Gets user groups, using cache if available.
   * @param forceRefresh - Force a fresh fetch even if cached
   * @returns Observable of user groups
   */
  getUserGroups(forceRefresh = false): Observable<readonly UserGroup[]> {
    // Check cache first
    if (!forceRefresh && this.userGroupsCache && !this.isExpired(this.userGroupsCache)) {
      return of(this.userGroupsCache.data);
    }

    // Check for in-flight request
    if (this.userGroupsRequest$) {
      return this.userGroupsRequest$;
    }

    // Update loading state
    this.userGroupsState.update((state) => ({ ...state, loading: true, error: null }));

    // Make new request
    this.userGroupsRequest$ = this.http.get<UserGroup[]>('api/security/groups').pipe(
      tap((data) => {
        const now = Date.now();
        this.userGroupsCache = {
          data,
          timestamp: now,
          expiresAt: now + DEFAULT_TTL_MS,
        };
        this.userGroupsState.set({ loading: false, data, error: null });
        this.userGroupsRequest$ = null;
      }),
      catchError((error) => {
        this.userGroupsState.update((state) => ({
          ...state,
          loading: false,
          error: error.message || 'Failed to load user groups',
        }));
        this.userGroupsRequest$ = null;
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    return this.userGroupsRequest$;
  }

  /**
   * Clears the user groups cache.
   */
  clearUserGroupsCache(): void {
    this.userGroupsCache = null;
    this.userGroupsRequest$ = null;
    this.userGroupsState.set({ loading: false, data: null, error: null });
  }

  // ============================================
  // Answer Templates Cache
  // ============================================

  private answerTemplatesCache: CacheEntry<readonly AnswerTemplate[]> | null = null;
  private answerTemplatesRequest$: Observable<readonly AnswerTemplate[]> | null = null;
  private readonly answerTemplatesState = signal<CacheState<readonly AnswerTemplate[]>>({
    loading: false,
    data: null,
    error: null,
  });

  /** Read-only signal for answer templates cache state */
  readonly answerTemplates = this.answerTemplatesState.asReadonly();

  /** Computed signal for just the answer templates data */
  readonly answerTemplatesData = computed(() => this.answerTemplatesState().data ?? []);

  /** Computed signal for answer templates loading state */
  readonly answerTemplatesLoading = computed(() => this.answerTemplatesState().loading);

  /**
   * Gets answer templates, using cache if available.
   * @param forceRefresh - Force a fresh fetch even if cached
   * @returns Observable of answer templates
   */
  getAnswerTemplates(forceRefresh = false): Observable<readonly AnswerTemplate[]> {
    // Check cache first
    if (
      !forceRefresh &&
      this.answerTemplatesCache &&
      !this.isExpired(this.answerTemplatesCache)
    ) {
      return of(this.answerTemplatesCache.data);
    }

    // Check for in-flight request
    if (this.answerTemplatesRequest$) {
      return this.answerTemplatesRequest$;
    }

    // Update loading state
    this.answerTemplatesState.update((state) => ({ ...state, loading: true, error: null }));

    // Make new request
    this.answerTemplatesRequest$ = this.http
      .get<AnswerTemplate[]>('api/question/answertemplates')
      .pipe(
        tap((data) => {
          const now = Date.now();
          this.answerTemplatesCache = {
            data,
            timestamp: now,
            expiresAt: now + DEFAULT_TTL_MS,
          };
          this.answerTemplatesState.set({ loading: false, data, error: null });
          this.answerTemplatesRequest$ = null;
        }),
        catchError((error) => {
          this.answerTemplatesState.update((state) => ({
            ...state,
            loading: false,
            error: error.message || 'Failed to load answer templates',
          }));
          this.answerTemplatesRequest$ = null;
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    return this.answerTemplatesRequest$;
  }

  /**
   * Clears the answer templates cache.
   * Call this after saving/deleting answer templates.
   */
  clearAnswerTemplatesCache(): void {
    this.answerTemplatesCache = null;
    this.answerTemplatesRequest$ = null;
    this.answerTemplatesState.set({ loading: false, data: null, error: null });
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Checks if a cache entry has expired.
   */
  private isExpired<T>(entry: CacheEntry<T>): boolean {
    return Date.now() > entry.expiresAt;
  }

  /**
   * Clears all caches.
   * Useful on logout or when data needs to be refreshed.
   */
  clearAllCaches(): void {
    this.clearUserGroupsCache();
    this.clearAnswerTemplatesCache();
  }
}
